import React, { useState, useEffect } from 'react';
import { useLanguage } from '../utils/LanguageContext';
import { createGuest, getGuests, Guest } from '../utils/api';

const formatDate = (date: string, language: string) => {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
    };
    
    return new Date(date).toLocaleDateString(
        language === 'hu' ? 'hu-HU' : 'en-US', 
        options
    );
};

const AreYouComing: React.FC = () => {
    const { t, language } = useLanguage();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [date, setDate] = useState('');
    const [notes, setNotes] = useState('');
    const [guests, setGuests] = useState<Guest[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Set date constraints (e.g., allow selecting dates only in March 2024)
    const minDate = '2025-05-16';
    const maxDate = '2025-05-18';

    useEffect(() => {
        // Load existing guests when component mounts
        const loadGuests = async () => {
            try {
                const loadedGuests = await getGuests();
                setGuests(loadedGuests);
            } catch (err) {
                setError(t('rsvp.error.loading'));
            }
        };
        loadGuests();
    }, [t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const newGuest = await createGuest({
                name,
                email,
                phone,
                date
            });
            setGuests([...guests, newGuest]);
            
            // Reset form
            setName('');
            setEmail('');
            setPhone('');
            setDate('');
            setNotes('');
            setError(null);
        } catch (err) {
            setError(t('rsvp.error.submit'));
        }
    };

    return (
        <div className="split-section">
            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    <h2>{t('rsvp.title')}</h2>
                    {error && <div className="error-message">{error}</div>}
                    <div className="form-group">
                        <label htmlFor="name">{t('rsvp.name')}</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-field"
                            required
                            placeholder={t('rsvp.name')}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="email">{t('rsvp.email')}</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-field"
                            required
                            placeholder={t('rsvp.email')}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="phone">{t('rsvp.phone')}</label>
                        <input
                            type="tel"
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="input-field"
                            required
                            placeholder={t('rsvp.phone')}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="date">{t('rsvp.date')}</label>
                        <input
                            type="date"
                            id="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            min={minDate}
                            max={maxDate}
                            className="input-field"
                            required
                            lang={language}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="notes">{t('rsvp.notes')}</label>
                        <textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="input-field"
                            rows={3}
                            placeholder={t('rsvp.notes.placeholder')}
                        />
                    </div>
                    <button type="submit" className="button">
                        {t('rsvp.submit')}
                    </button>
                </form>
            </div>

            <div className="attendees-list">
                <h2>{t('rsvp.guestlist.title')}</h2>
                <div className="attendance-stats">
                    <span>{t('rsvp.guestlist.total')} {guests.length} {t('rsvp.guestlist.guests')}</span>
                </div>
                <div className="attendees-grid">
                    {guests.map((guest, index) => (
                        <div key={index} className="attendee-card">
                            <h3>{guest.name}</h3>
                            <span className="date">
                                {t('rsvp.guestlist.coming')} {formatDate(guest.date, language)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AreYouComing;